const getOrCreateHeadElement = ({ selector, tagName, attributes }) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    document.head.appendChild(element);
  }

  return element;
};

export function applyWebSeo(seo) {
  const previousTitle = document.title;
  document.title = seo.title;

  const description = getOrCreateHeadElement({
    selector: 'meta[name="description"]',
    tagName: "meta",
    attributes: { name: "description" },
  });
  const previousDescription = description.getAttribute("content");
  description.setAttribute("content", seo.description);

  const keywords = getOrCreateHeadElement({
    selector: 'meta[name="keywords"]',
    tagName: "meta",
    attributes: { name: "keywords" },
  });
  const previousKeywords = keywords.getAttribute("content");
  keywords.setAttribute("content", seo.keywords);

  const hadRobots = Boolean(document.head.querySelector('meta[name="robots"]'));
  const robots = getOrCreateHeadElement({
    selector: 'meta[name="robots"]',
    tagName: "meta",
    attributes: { name: "robots" },
  });
  const previousRobots = robots.getAttribute("content");
  robots.setAttribute("content", seo.robots);

  const hadCanonical = Boolean(document.head.querySelector('link[rel="canonical"]'));
  const canonical = getOrCreateHeadElement({
    selector: 'link[rel="canonical"]',
    tagName: "link",
    attributes: { rel: "canonical" },
  });
  const previousCanonical = canonical.getAttribute("href");
  canonical.setAttribute("href", seo.canonical);

  const socialTags = [
    { selector: 'meta[property="og:title"]', tagName: "meta", attributes: { property: "og:title" }, content: seo.openGraph.title },
    { selector: 'meta[property="og:description"]', tagName: "meta", attributes: { property: "og:description" }, content: seo.openGraph.description },
    { selector: 'meta[property="og:url"]', tagName: "meta", attributes: { property: "og:url" }, content: seo.openGraph.url },
    { selector: 'meta[property="og:type"]', tagName: "meta", attributes: { property: "og:type" }, content: seo.openGraph.type },
    { selector: 'meta[property="og:image"]', tagName: "meta", attributes: { property: "og:image" }, content: seo.openGraph.image },
    { selector: 'meta[property="og:site_name"]', tagName: "meta", attributes: { property: "og:site_name" }, content: seo.openGraph.siteName },
    { selector: 'meta[property="og:locale"]', tagName: "meta", attributes: { property: "og:locale" }, content: seo.openGraph.locale },
    { selector: 'meta[name="twitter:card"]', tagName: "meta", attributes: { name: "twitter:card" }, content: seo.twitter.card },
    { selector: 'meta[name="twitter:title"]', tagName: "meta", attributes: { name: "twitter:title" }, content: seo.twitter.title },
    { selector: 'meta[name="twitter:description"]', tagName: "meta", attributes: { name: "twitter:description" }, content: seo.twitter.description },
    { selector: 'meta[name="twitter:url"]', tagName: "meta", attributes: { name: "twitter:url" }, content: seo.twitter.url },
    { selector: 'meta[name="twitter:image"]', tagName: "meta", attributes: { name: "twitter:image" }, content: seo.twitter.image },
  ];

  const previousSocialTags = socialTags.map((tag) => {
    const hadElement = Boolean(document.head.querySelector(tag.selector));
    const element = getOrCreateHeadElement(tag);
    const previousContent = element.getAttribute("content");
    element.setAttribute("content", tag.content);
    return { element, hadElement, previousContent };
  });

  const jsonLd = getOrCreateHeadElement({
    selector: `script[data-web-seo-json-ld="${seo.id}"]`,
    tagName: "script",
    attributes: { type: "application/ld+json", "data-web-seo-json-ld": seo.id },
  });
  jsonLd.textContent = JSON.stringify(seo.jsonLd);

  return () => {
    document.title = previousTitle;
    if (previousDescription) description.setAttribute("content", previousDescription);
    if (previousKeywords) {
      keywords.setAttribute("content", previousKeywords);
    } else {
      keywords.remove();
    }
    if (previousRobots) {
      robots.setAttribute("content", previousRobots);
    } else if (!hadRobots) {
      robots.remove();
    }
    if (previousCanonical) {
      canonical.setAttribute("href", previousCanonical);
    } else if (!hadCanonical) {
      canonical.remove();
    }
    previousSocialTags.forEach(({ element, hadElement, previousContent }) => {
      if (previousContent) {
        element.setAttribute("content", previousContent);
      } else if (!hadElement) {
        element.remove();
      }
    });
    jsonLd.remove();
  };
}
